import React, { useEffect, useState } from 'react';
import excelNotificationService, { ExcelNotification } from '../services/excelNotificationService';
import { directExcelNotification } from '../services/directExcelNotification';
import { useAuth } from '../context/AuthContext';
import './ExcelNotificationBell.css';

const ExcelNotificationBell: React.FC = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<ExcelNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadNotifications();
    }
  }, [user]);

  // Refresh notifications every 5 seconds
  useEffect(() => {
    if (user) {
      const interval = setInterval(() => {
        loadNotifications();
      }, 5000);
      
      return () => clearInterval(interval);
    }
  }, [user]);

  const loadNotifications = async () => {
    if (!user) {
      console.log('🔔 No user logged in, skipping notification load');
      return;
    }
    
    try {
      console.log(`🔔 Loading DIRECT notifications for user: ${user.uid} (${user.displayName || user.email})`);
      setIsLoading(true);
      
      // Load DIRECT notifications
      const directNotifications = await directExcelNotification.getUserExcelNotifications(user.uid);
      console.log(`🔔 Loaded ${directNotifications.length} DIRECT notifications:`, directNotifications);
      
      // Convert direct notifications to ExcelNotification format
      const formattedNotifications: any[] = directNotifications.map(n => ({
        id: n.id,
        userId: n.userId,
        type: n.type,
        title: n.title,
        message: n.message,
        createdAt: n.createdAt,
        read: n.read,
        data: {
          sheetName: n.excelData.sheetName,
          itemId: n.excelData.workbookId,
          workbookId: n.excelData.workbookId,
          headers: n.excelData.headers,
          rows: n.excelData.rows,
          timestamp: n.createdAt,
          completeRowData: n.excelData.rows[0] ? Object.fromEntries(
            n.excelData.headers.map((header, i) => [header, n.excelData.rows[0][i] || ''])
          ) : {}
        }
      }));
      
      console.log(`🔔 Formatted ${formattedNotifications.length} notifications for display`);
      setNotifications(formattedNotifications);
      setUnreadCount(formattedNotifications.filter(n => !n.read).length);
    } catch (error) {
      console.error('Error loading DIRECT notifications:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNotificationClick = async (notification: ExcelNotification) => {
    if (!notification.read) {
      await directExcelNotification.markAsRead(notification.id!, notification.userId);
      setUnreadCount(prev => prev - 1);
      setNotifications(prev => 
        prev.map(n => n.id === notification.id ? { ...n, read: true } : n)
      );
    }
  };

  const handleMarkAllAsRead = async () => {
    if (!user) return;
    
    try {
      await excelNotificationService.markAllAsRead(user.uid);
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return date.toLocaleDateString();
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'EXCEL_UPDATE':
        return '📝';
      case 'EXCEL_ASSIGNMENT':
        return '👤';
      case 'EXCEL_COMMENT':
        return '💬';
      default:
        return '📊';
    }
  };

  const getNotificationColor = (type: string) => {
    switch (type) {
      case 'EXCEL_UPDATE':
        return '#007bff';
      case 'EXCEL_ASSIGNMENT':
        return '#28a745';
      case 'EXCEL_COMMENT':
        return '#ffc107';
      default:
        return '#6c757d';
    }
  };

  if (!user) return null;

  return (
    <div className="excel-notification-bell">
      <button
        className="notification-bell-button"
        onClick={() => setShowDropdown(!showDropdown)}
        title="Excel Notifications"
      >
        <span className="bell-icon">🔔</span>
        {unreadCount > 0 && (
          <span className="notification-badge">{unreadCount}</span>
        )}
      </button>

      {showDropdown && (
        <div className="notification-dropdown">
          <div className="notification-header">
            <h3>Excel Notifications</h3>
            <div className="notification-actions">
              <button
                className="refresh-btn"
                onClick={loadNotifications}
                title="Refresh notifications"
              >
                🔄
              </button>
              <button
                className="test-btn"
                onClick={async () => {
                  if (user) {
                    console.log('🧪 Creating test notification...');
                    await excelNotificationService.createTestNotification(user.uid);
                    await loadNotifications();
                  }
                }}
                title="Create test notification"
                style={{ background: '#ff6b6b', color: 'white', marginLeft: '5px' }}
              >
                🧪 Test
              </button>
              {unreadCount > 0 && (
                <button
                  className="mark-all-read-btn"
                  onClick={handleMarkAllAsRead}
                >
                  Mark all as read
                </button>
              )}
            </div>
          </div>

          <div className="notification-list">
            {isLoading ? (
              <div className="loading-notifications">
                <div className="loading-spinner"></div>
                <p>Loading notifications...</p>
              </div>
            ) : notifications.length === 0 ? (
              <div className="no-notifications">
                <p>No notifications yet</p>
              </div>
            ) : (
              notifications.map(notification => (
                <div
                  key={notification.id}
                  className={`notification-item ${!notification.read ? 'unread' : ''}`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="notification-icon">
                    {getNotificationIcon(notification.type)}
                  </div>
                  <div className="notification-content">
                    <div className="notification-title">
                      {notification.title}
                    </div>
                    <div className="notification-message">
                      {notification.message}
                    </div>
                    <div className="notification-details">
                      {notification.data.fieldName && (
                        <div className="field-info">
                          <strong>Field:</strong> {notification.data.fieldName}
                        </div>
                      )}
                      {notification.data.fieldValue && (
                        <div className="field-value">
                          <strong>Value:</strong> {notification.data.fieldValue}
                        </div>
                      )}
                      {notification.data.assignedBy && (
                        <div className="assigned-by">
                          <strong>Assigned by:</strong> {notification.data.assignedBy}
                        </div>
                      )}
                    </div>
                    <div className="notification-meta">
                      <span className="notification-time">
                        {formatTimestamp(notification.createdAt)}
                      </span>
                      {notification.data.cellAddress && (
                        <span className="notification-cell">
                          {notification.data.cellAddress}
                        </span>
                      )}
                    </div>
                  </div>
                  {!notification.read && (
                    <div className="unread-indicator"></div>
                  )}
                </div>
              ))
            )}
          </div>

          <div className="notification-footer">
            <button
              className="refresh-btn"
              onClick={loadNotifications}
            >
              🔄 Refresh
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExcelNotificationBell;



